import React, { useState, useEffect, useRef } from "react";
import { Card, CardTitle, Bar, Badge } from "../UI";
import { ANALYSIS_DONE_STATUSES, BRANDS_COLORS, FUNNEL_STAGES, font } from "./constants";
import { getAiSimulationDetails } from "../../requests";
import styles from "../../_css/Simulation.module.css";
import Empty from "./Empty";
import NextButton from "./NextButton";
import {
  pickLatestAiSimulationRun,
  describePlatformModel,
} from "./simulationRunHelpers";
import { isRTL } from "../../utils/helper";
import { isSimulationLanguageEnglish } from "./simulationTranslationStorage";

import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import useRole from "../../hooks/getRole";
import { Play } from "lucide-react";

const SHOW_TRANSLATE_BUTTON = true;

const safeArray = (v) => (Array.isArray(v) ? v : []);
const safeObject = (v) =>
  v && typeof v === "object" && !Array.isArray(v) ? v : {};
const safeString = (v) => (typeof v === "string" ? v.trim() : "");
const safeNum = (v, def = 0) =>
  typeof v === "number" && !Number.isNaN(v)
    ? v
    : typeof v === "string"
      ? parseFloat(v) || def
      : def;
const safeKeys = (obj) =>
  obj && typeof obj === "object" && !Array.isArray(obj) ? Object.keys(obj) : [];
const na = (v, fallback = "Not available") =>
  v != null && v !== "" && (typeof v !== "string" || v.trim() !== "")
    ? v
    : fallback;

const PLATFORM_OPTIONS = [
  { id: "chatgpt", label: "ChatGPT", color: "#10a37f" },
  { id: "claude", label: "Claude", color: "#d97706" },
  { id: "gemini", label: "Gemini", color: "#4285f4" },
  { id: "perplexity", label: "Perplexity", color: "#1a1a2e" },
];

function CompetitiveTab({
  recentSimulationId = null,
  recentSimulationDetails = null,
  recentSimulationStatus = null,
  selectedRun = null,
  onReturnToLatestRun = () => {},
  activeFunnel = "awareness",
  translateLanguage = null,
  translationViewLang = "or",
  onGoToRuns = null,
}) {
  const context = useSelector((state) => state.context);
  const userInfo = useSelector((state) => state.user);
  const role = useRole();
  const location = useLocation();
  const navigate = useNavigate();
  const translationViewLangRef = useRef(translationViewLang);
  const [activePlatform, setActivePlatform] = useState("chatgpt");
  const [loading, setLoading] = useState(true);
  const [details, setDetails] = useState(null);
  const [translating, setTranslating] = useState(false);
  const [viewingTranslatedQueries, setViewingTranslatedQueries] = useState(
    translationViewLang === "en",
  );

  useEffect(() => {
    const translationParam = new URLSearchParams(location.search).get(
      "translate",
    );
    const isTranslation = translationParam === "true";
    if (isTranslation) {
      setViewingTranslatedQueries(true);
      translationViewLangRef.current = "en";
      return;
    }
    setViewingTranslatedQueries(translationViewLang === "en");
  }, [translationViewLang, location.search]);

  useEffect(() => {
    const translationParam = new URLSearchParams(location.search).get(
      "translate",
    );
    const isTranslation = translationParam === "true";

    // A specific historical run was explicitly picked in the Runs tab — show it regardless of
    // the simulation's live status, instead of falling through to the "always latest" fetch below.
    if (selectedRun) {
      setDetails({ ...(recentSimulationDetails ?? {}), status: 6 });
      setLoading(false);
      return;
    }

    if (
      recentSimulationDetails &&
      (recentSimulationStatus === 6 || recentSimulationStatus === 91)
    ) {
      setDetails(recentSimulationDetails);
      setLoading(false);
      return;
    }
    if (!recentSimulationId) {
      setDetails(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const showTranslation = isTranslation ? true : translationViewLang === "en";
    getAiSimulationDetails(recentSimulationId, showTranslation)
      .then((res) => setDetails(res?.data ?? null))
      .catch(() => setDetails(null))
      .finally(() => setLoading(false));
  }, [
    recentSimulationId,
    recentSimulationDetails,
    recentSimulationStatus,
    translationViewLang,
    location.search,
    selectedRun,
  ]);

  const handleTranslateLanguage = async () => {
    if (!translateLanguage || translating) return;
    setTranslating(true);
    try {
      const lang = viewingTranslatedQueries ? "or" : "en";
      translationViewLangRef.current = lang;
      const response = await translateLanguage(lang);
      if (response) {
        const base = `/simulations/details/${recentSimulationId}/competitive?simulation_id=${recentSimulationId}&project_id=${context?.project_id}&brand_id=${context?.brand_id}`;
        if (role === "super_admin") {
          if (lang === "en") {
            navigate(`${base}&agency_id=${userInfo?.agencyId}&translate=true`);
          } else {
            navigate(`${base}&agency_id=${userInfo?.agencyId}&translate=false`);
          }
        } else {
          if (lang === "en") {
            navigate(`${base}&translate=true`);
          } else {
            navigate(`${base}&translate=false`);
          }
        }
      }
    } finally {
      setTranslating(false);
    }
  };

  if (!recentSimulationId) {
    return (
      <Empty message="Failed simulation. Please create a new simulation again" />
    );
  }

  if (details?.status == -1) {
    return (
      <Empty message="Failed simulation. Please create a new simulation again" />
    );
  }

  if (details?.status == 1 || details?.status == 2 || details?.status == 3) {
    return (
      <Empty message="Queries generation in progress..." type="progress" />
    );
  }
  if (details?.status == 4) {
    return (
      <Empty
        message="Queries generation completed. Please go to Queries tab and select the queries you want to run. Then click on the Run Simulation button to start the simulation."
        type="progress"
      />
    );
  }

  // A new run may be processing (status 5/90) or in some other transient state, but if an
  // earlier run already completed, keep showing its data instead of blocking the whole tab.
  const hasCompletedRun = safeArray(details?.aiSimulationRuns).length > 0;

  if ((details?.status == 5 || details?.status == 90) && !hasCompletedRun) {
    return (
      <Empty message="Competitive analysis in progress..." type="progress" />
    );
  }

  if (!ANALYSIS_DONE_STATUSES.includes(Number(details?.status)) && !hasCompletedRun) {
    return (
      <Empty
        message="AI is still analyzing the data. Please wait for a moment."
        type="progress"
      />
    );
  }

  const run =
    pickLatestAiSimulationRun(details?.aiSimulationRuns) ??
    details?.aiSimulationRuns?.[0] ??
    null;

  // Per-platform breakdown (ai_simulation_run_models): one row per LLM platform for this run.
  // Falls back to the run's own (legacy, chatgpt-only) columns when no per-platform row exists yet.
  const platformModels = safeArray(run?.ai_simulation_run_models);
  const activePlatformModel =
    platformModels.find((m) => m?.platform === activePlatform) ?? null;
  const dataSource =
    activePlatformModel ?? (activePlatform === "chatgpt" ? run : null);
  const hasPlatformData = dataSource != null;
  // Which model answered on each platform, read from the saved run
  // (same data rule as dataSource above). null when the platform has no data in this run.
  const modelInfoFor = (platformId) => {
    const row = platformModels.find((m) => m?.platform === platformId) ?? null;
    // The run itself only stands in for ChatGPT on legacy runs without per-platform rows
    const hasData =
      row != null || (platformId === "chatgpt" && run != null && platformModels.length === 0);
    return hasData ? describePlatformModel(row, platformId) : null;
  };
  const activeModelInfo = modelInfoFor(activePlatform);

  const insights = safeObject(dataSource?.extracted_insights);
  const competitiveLandscape = safeObject(insights?.competitive_landscape);

  // Use only allowed data: share_of_voice, thematic_analysis, authority_analysis, strategic_analysis (except top_objections, strategic_recommendations),
  // visibility_dynamics, competitive_landscape, priority_opportunities, competitive_gap_analysis. Exclude: client_mention_rate, client_share_of_voice,
  // client_avg_position, total_queries_executed, total_queries_with_mention, summary_metrics, sentiment_distribution, top_objections, strategic_recommendations.
  // extracted_insights.weighted_share_of_voice is never populated today (see prior investigation) —
  // fall back to the confirmed-real results.cross_platform.platforms.<platform>.sov map.
  const sovData =
    insights?.weighted_share_of_voice ??
    run?.results?.cross_platform?.platforms?.[activePlatform]?.sov;
  // console.log("invoice", insights);
  const isSovArray = Array.isArray(sovData);
  const isSovObject =
    sovData && typeof sovData === "object" && !Array.isArray(sovData);
  const hasSov = isSovArray
    ? sovData.length > 0
    : isSovObject
      ? safeKeys(sovData).length > 0
      : false;

  const gapDataRaw = insights?.competitive_gap_analysis;
  const gapData = Array.isArray(gapDataRaw)
    ? gapDataRaw
    : gapDataRaw != null && typeof gapDataRaw === "object"
      ? gapDataRaw
      : null;
  const hasGap = Array.isArray(gapData)
    ? gapData.length > 0
    : gapData != null &&
      typeof gapData === "object" &&
      safeKeys(gapData).length > 0;

  const mostFrequentCompetitors = safeArray(
    competitiveLandscape?.most_frequent_competitors,
  );

  const sovEntries = isSovArray
    ? (sovData || [])
        .map((e) => ({
          name: String(e?.brand ?? e?.name ?? e ?? "—").trim() || "—",
          sov: safeNum(e?.percentage ?? e?.sov ?? e?.share, 0),
          mentions: safeNum(e?.queries_mentioned ?? e?.mentions_count, 0),
        }))
        .filter((x) => x.name && x.name !== "—")
    : isSovObject
      ? Object.entries(sovData || {})
          .map(([name, val]) => ({
            name: String(name || "—").trim() || "—",
            sov: safeNum(val, 0),
            mentions: safeNum(val, 0),
          }))
          .filter((x) => x.name && x.name !== "—")
      : [];
  const sortedSov = [...sovEntries].sort(
    (a, b) => safeNum(b.sov) - safeNum(a.sov),
  );

  const isRTLanguage =
    translationViewLangRef.current === "en"
      ? false
      : isRTL(details?.language ?? recentSimulationDetails?.language ?? "");
  const rtlDir = isRTLanguage ? "rtl" : "ltr";
  const cardTitleWeight = isRTLanguage ? 300 : 600;
  const stageInfo = (Array.isArray(FUNNEL_STAGES)
    ? FUNNEL_STAGES.find((s) => s?.id === activeFunnel)
    : null) ??
    FUNNEL_STAGES?.[0] ?? {
      id: "awareness",
      label: "Awareness",
      icon: "👁",
      desc: "—",
    };

  const platformPickerEl = (
    <div className={styles.platformPicker}>
      <div className={styles.platformPickerRow}>
        <span className={styles.platformLabel}>LLM Platform</span>
        <div className={styles.platformOptions}>
          <div
            className={styles.platformThumb}
            style={{
              transform: `translateX(${PLATFORM_OPTIONS.findIndex((p) => p.id === activePlatform) * 100}%)`,
            }}
          />
          {PLATFORM_OPTIONS.map((platform) => {
            const selected = platform.id === activePlatform;
            return (
              <button
                key={platform.id}
                type="button"
                title={modelInfoFor(platform.id)?.title || undefined}
                className={`${styles.platformOption} ${
                  selected ? styles.platformOptionActive : ""
                }`}
                onClick={() => setActivePlatform(platform.id)}
              >
                <span
                  className={styles.platformDot}
                  style={{
                    background: platform.color,
                    opacity: selected ? 1 : 0.45,
                  }}
                />
                <span className={styles.platformName}>{platform.label}</span>
              </button>
            );
          })}
        </div>
      </div>
      <span className={styles.platformHint}>
        Showing results for{" "}
        {PLATFORM_OPTIONS.find((p) => p.id === activePlatform)?.label ||
          "ChatGPT"}
        {activeModelInfo?.label && (
          <>
            {" · "}
            <bdi
              dir="ltr"
              className={styles.platformModelId}
              title={activeModelInfo.title}
            >
              {activeModelInfo.label}
            </bdi>
          </>
        )}
      </span>
    </div>
  );

  if (!hasPlatformData) {
    const platformLabel =
      PLATFORM_OPTIONS.find((p) => p.id === activePlatform)?.label ??
      activePlatform;
    return (
      <div className={styles.tabPage}>
        {platformPickerEl}
        <div className={styles.tabHeaderRow}>
          <div>
            <h2 className={styles.personaProfileTitle}>
              Competitive Insights
            </h2>
          </div>
        </div>
        <Empty
          message={`No ${platformLabel} analysis available yet for this simulation. Results are generated per platform as the simulation completes.`}
        />
      </div>
    );
  }

  return (
    <div className={styles.tabPage} dir={rtlDir}>
      {SHOW_TRANSLATE_BUTTON &&
        translateLanguage &&
        ANALYSIS_DONE_STATUSES.includes(
          Number(details?.status ?? recentSimulationStatus),
        ) &&
        !isSimulationLanguageEnglish(
          details?.language ?? recentSimulationDetails?.language,
        ) && (
          <div
            style={{
              display: "flex",
              justifyContent: isRTLanguage ? "flex-start" : "flex-end",
              marginBottom: 8,
            }}
          >
            <button
              type="button"
              onClick={handleTranslateLanguage}
              disabled={translating}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "8px 16px",
                borderRadius: 8,
                border: "1px solid rgba(0,0,0,0.12)",
                background: "rgba(0,0,0,0.04)",
                color: "#363636",
                fontSize: 13,
                fontWeight: 600,
                cursor: translating ? "not-allowed" : "pointer",
                fontFamily: font,
                opacity: translating ? 0.85 : 1,
              }}
            >
              {translating && (
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  aria-hidden
                  style={{ flexShrink: 0 }}
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="rgba(0,0,0,0.12)"
                    strokeWidth="3"
                    fill="none"
                  />
                  <path
                    d="M12 2a10 10 0 0 1 10 10"
                    stroke="#363636"
                    strokeWidth="3"
                    fill="none"
                    strokeLinecap="round"
                  >
                    <animateTransform
                      attributeName="transform"
                      type="rotate"
                      from="0 12 12"
                      to="360 12 12"
                      dur="0.75s"
                      repeatCount="indefinite"
                    />
                  </path>
                </svg>
              )}
              {translating
                ? "Translating…"
                : viewingTranslatedQueries
                  ? "Show original"
                  : "Translate to English"}
            </button>
          </div>
        )}
      {platformPickerEl}

      <div className={styles.tabHeaderRow}>
        <div>
          <h2 className={styles.personaProfileTitle}>Competitive Insights</h2>
        </div>
        </div>
      {selectedRun && (
        <div
          onClick={onReturnToLatestRun}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#b45309',
            background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.3)',
            borderRadius: 999, padding: '4px 10px', marginBottom: 12, cursor: 'pointer',
          }}
        >
          ← Viewing Run #{selectedRun.id} — click to return to latest
        </div>
      )}
      <Card className={styles.responseFunnelCard}>
        <span className={styles.responseFunnelIcon}>
          {stageInfo?.icon != null && stageInfo?.icon !== ""
            ? stageInfo.icon
            : "Not available"}
        </span>
        <span className={styles.responseFunnelLabel}>
          Funnel Stage: {na(stageInfo?.label)}
        </span>
        <span className={styles.responseFunnelDesc}>
          — {na(stageInfo?.desc)}
        </span>
      </Card>

      <Card>
        <CardTitle fontWeight={cardTitleWeight}>
          Competitive Gap Analysis
        </CardTitle>
        {!hasGap ? (
          <p className={styles.compTabMuted}>Not available</p>
        ) : (
          <div className={styles.compGapList}>
            {Array.isArray(gapData) ? (
              gapData.map((c, i) => {
                const gapName =
                  c != null ? safeString(c?.competitor ?? c?.name) || "—" : "—";
                const colors = safeObject(BRANDS_COLORS);
                const strength =
                  c?.strengths != null
                    ? String(c.strengths)
                    : c?.strength != null
                      ? String(c.strength)
                      : "";
                const rec =
                  c?.recommendation != null
                    ? String(c.recommendation)
                    : c?.rec != null
                      ? String(c.rec)
                      : "";
                const summary = c?.summary != null ? String(c.summary) : "";
                return (
                  <div
                    key={`gap-${i}-${gapName}`}
                    className={styles.compGapItem}
                    style={{
                      borderInlineStart: `3px solid ${colors[gapName] ?? "#6b7280"}`,
                    }}
                  >
                    <div className={styles.compGapHeader}>
                      <span className={styles.compGapName}>{gapName}</span>
                    </div>
                    {strength && (
                      <div className={styles.compGapStrength}>
                        Strength: {strength}
                      </div>
                    )}
                    {rec && (
                      <div className={styles.compGapRec}>
                        Recommendation: {rec}
                      </div>
                    )}
                    {summary && (
                      <div className={styles.compGapSummary}>{summary}</div>
                    )}
                  </div>
                );
              })
            ) : typeof gapData === "string" ? (
              <div
                className={styles.compGapItem}
                style={{ borderInlineStart: "3px solid #6b7280" }}
              >
                <div className={styles.compGapRec}>{gapData}</div>
              </div>
            ) : (
              <p className={styles.compTabMuted}>Not available</p>
            )}
          </div>
        )}
      </Card>

      {mostFrequentCompetitors.length > 0 ? (
        <Card>
          <CardTitle sub="From extracted insights" fontWeight={cardTitleWeight}>
            Most Frequent Competitors
          </CardTitle>
          <div className={styles.compBadgeWrap}>
            {mostFrequentCompetitors.map((c, i) => {
              const label =
                c != null
                  ? typeof c === "string"
                    ? c
                    : safeString(c?.brand ?? c?.name ?? c) || String(c)
                  : "—";
              if (!label || label === "—") return null;
              const colors = safeObject(BRANDS_COLORS);
              return (
                <span key={`comp-${i}-${label}`}>
                  <Badge color={colors[label] ?? "#6b7280"}>{label}</Badge>
                </span>
              );
            })}
          </div>
        </Card>
      ) : (
        <Card>
          <CardTitle sub="From extracted insights" fontWeight={cardTitleWeight}>
            Most Frequent Competitors
          </CardTitle>
          <p className={styles.compTabMuted}>Not available</p>
        </Card>
      )}

      <Card>
        <CardTitle
          sub="Based on all simulated responses"
          fontWeight={cardTitleWeight}
        >
          Share of Voice
        </CardTitle>
        {!hasSov ? (
          <p className={styles.compTabMuted}>Not available</p>
        ) : (
          <div className={styles.compSovList}>
            {sortedSov.map((b, i) => {
              const name = b?.name ?? "—";
              const sovVal = safeNum(b?.sov);
              const colors = safeObject(BRANDS_COLORS);
              return (
                <div key={`sov-${i}-${name}`} className={styles.compSovRow}>
                  <span className={styles.compSovRank}>{i + 1}</span>
                  <span className={styles.compSovName}>{name}</span>
                  <div className={styles.compSovBar}>
                    <Bar
                      pct={Math.min(100, Math.max(0, sovVal))}
                      color={colors[name] ?? "#6b7280"}
                      showLabel={false}
                      height={8}
                    />
                  </div>
                  <span className={styles.compSovPct}>
                    {sovVal != null ? `${sovVal}%` : "—"}
                  </span>
                  {b?.mentions != null && (
                    <span className={styles.compSovMentions}>
                      {safeNum(b.mentions)} mentions
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>
      {typeof onGoToRuns === "function" && (
        <div className={styles.personaActions}>
          <NextButton
            label="Continue to Runs"
            onClick={onGoToRuns}
            icon={<Play size={16} />}
          />
        </div>
      )}
    </div>
  );
}

export default CompetitiveTab;
